import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, ArrowRight } from "lucide-react";
import api, { capi, formatApiError, getCustomerProfile, clearCustomerSession } from "@/lib/api";
import { CustomerGate } from "@/components/CustomerAuth";
import { toast } from "sonner";
import NotifyForm from "@/components/NotifyForm";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const MARQUEE_ITEMS = [
  "Pure Australian Botanicals",
  "Small Batch Formulation",
  "Cruelty Free",
  "Dermatologically Tested",
  "Recyclable Glass",
  "Now Open",
];

const CHAPTERS = [
  { n: "01", title: "Purity", text: "Every formula begins with pristine Australian botanicals — cold-pressed, traceable, uncompromised." },
  { n: "02", title: "Provenance", text: "Blended in small batches on Australian shores. From our hands, to your ritual." },
  { n: "03", title: "Radiance", text: "Skincare as ceremony. Quiet, considered, and made to be felt — morning and night." },
];

const FeaturedCollection = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 3))).catch(() => {});
  }, []);
  if (!products.length) return null;
  return (
    <section className="px-6 pb-28 md:pb-40" data-testid="featured-collection">
      <Reveal className="text-center mb-16">
        <p className="text-xs tracking-[0.5em] uppercase text-[#9E7B3A] mb-6">The Collection</p>
        <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">
          Signature <span className="italic text-[#9E7B3A]">rituals</span>
        </h2>
      </Reveal>
      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
        {products.map((p) => (
          <Reveal key={p.id}>
            <Link to={`/shop/product/${p.id}`} className="group block" data-testid={`featured-${p.id}`}>
              <div className="overflow-hidden bg-white border border-[#C5A059]/15 mb-4">
                <img src={p.image_url || "/assets/logo.png"} alt={p.name}
                  className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <h3 className="font-serif text-lg font-light group-hover:text-[#9E7B3A] transition-colors duration-300">{p.name}</h3>
              <p className="text-sm text-[#4A4A4A]">${p.price.toFixed(2)} AUD</p>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal className="text-center">
        <Link to="/shop" data-testid="view-collection-link"
          className="text-xs tracking-[0.3em] uppercase text-[#9E7B3A] border-b border-[#C5A059] pb-1 hover:text-[#1A1A1A] transition-colors duration-300">
          View the Full Collection
        </Link>
      </Reveal>
    </section>
  );
};

const DEPOSIT = 99;
const SQUARE_LINK = "https://square.link/u/FlI5wfG1";

const SERVICES = [
  { n: "01", group: "Makeup", title: "Special Occasion Makeup", time: "60 min", price: 95, text: "A flawless, long-wear look tailored to your event — soft glam to full glam, finished with lashes." },
  { n: "02", group: "Makeup", title: "Bridal Makeup", time: "90 min", price: 165, text: "Your dream bridal look — perfected at a full trial, then perfected again on the day." },
  { n: "03", group: "Facials", title: "Express Glow Facial", time: "30 min", price: 49, text: "Double cleanse, exfoliation and a hydration boost — the perfect lunch-break refresh." },
  { n: "04", group: "Facials", title: "Signature Luxe Facial", time: "60 min", price: 119, text: "A deeply restorative facial tailored to your skin — double cleanse, enzymatic exfoliation, sculpting massage and a bespoke mask." },
  { n: "05", group: "Facials", title: "Deluxe Sculpt & Glow Facial", time: "90 min", price: 179, text: "Our signature facial elevated with buccal massage and lymphatic drainage for visible lift, contour and glow." },
  { n: "06", group: "Body Contouring", title: "Fat Cavitation — Single Area", time: "45 min", price: 129, text: "Ultrasound cavitation targets stubborn fat pockets — stomach, thighs, arms or flanks. A course of sessions is recommended for best results." },
  { n: "07", group: "Skin Tightening", title: "Skin Tightening — Face", time: "45 min", price: 159, text: "Radio-frequency tightening for the face and jawline — stimulates collagen for visibly firmer, lifted skin over a course of treatments." },
  { n: "08", group: "Skin Tightening", title: "Skin Tightening — Body", time: "60 min", price: 189, text: "Radio-frequency tightening for the body — firms and smooths skin on the stomach, arms, thighs or glutes." },
  { n: "09", group: "Body Contouring", title: "Ultimate Contour Package", time: "90 min", price: 319, text: "Fat cavitation and skin tightening combined in one transformative session — our most requested body treatment." },
];

const BookingModal = ({ service, onClose }) => {
  const [profile, setProfile] = useState(getCustomerProfile());
  const [form, setForm] = useState({ phone: "", preferred: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  if (!service) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await capi.post("/bookings", { ...form, service: service.title, price: service.price, deposit: DEPOSIT });
      if (data.success) setDone(true);
      else toast.error(data.message);
    } catch (err) {
      if (err?.response?.status === 401) { clearCustomerSession(); setProfile(null); toast.error("Please log in again"); }
      else toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#C5A059] pb-3 text-base placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#9E7B3A] transition-colors duration-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="booking-modal">
      <div className="bg-white max-w-lg w-full p-8 md:p-10 max-h-[90vh] overflow-y-auto shadow-[0_24px_64px_rgba(0,0,0,0.15)]" onClick={(e) => e.stopPropagation()}>
        {!done ? (
          <>
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#9E7B3A] mb-2">{service.group}</p>
            <h3 className="font-serif text-3xl font-light mb-1">{service.title}</h3>
            <p className="text-sm text-[#4A4A4A] mb-8">{service.time} · ${service.price} · ${DEPOSIT} deposit to book</p>
            {!profile ? (
              <CustomerGate heading="Step 1 — your account" onAuthed={setProfile} />
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <p className="text-sm text-[#4A4A4A] border-b border-[#C5A059]/30 pb-4" data-testid="booking-account-info">
                  Booking as <span className="font-medium text-[#1A1A1A]">{profile.name}</span> · {profile.email}
                </p>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" aria-label="Phone" data-testid="booking-phone" className={inputCls} />
                <input value={form.preferred} onChange={(e) => set("preferred", e.target.value)} placeholder="Preferred day & time (e.g. Saturday morning)" aria-label="Preferred time" data-testid="booking-preferred" className={inputCls} />
                <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything we should know?" aria-label="Message" data-testid="booking-message" className={`${inputCls} resize-none`} />
                <button type="submit" disabled={sending} data-testid="booking-submit"
                  className="group w-full flex items-center justify-center gap-3 bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-4 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
                  {sending ? "Sending…" : "Request Booking"}
                  <ArrowRight className="w-4 h-4 text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </>
        ) : (
          <div className="text-center" data-testid="booking-success">
            <div className="h-px w-16 bg-[#C5A059] mx-auto mb-6" />
            <p className="font-serif text-3xl font-light mb-3">Almost booked.</p>
            <p className="text-sm text-[#4A4A4A] mb-8">
              Secure your {service.title} with a ${DEPOSIT} deposit.{" "}
              {service.price - DEPOSIT > 0
                ? `The balance of $${service.price - DEPOSIT} is paid at your appointment.`
                : "Your deposit fully covers this treatment — any difference is credited or refunded at your appointment."}
            </p>
            <a href={SQUARE_LINK} target="_blank" rel="noopener noreferrer" data-testid="square-deposit-button"
              className="block w-full bg-[#1A1A1A] text-white text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#9E7B3A] transition-colors duration-300 mb-3">
              Pay ${DEPOSIT} Deposit + Card Fees
            </a>
            <p className="text-[11px] text-[#4A4A4A]/70 mb-6">Card payments via Square include transaction fees.</p>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-[#C5A059]/30" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]/60">or</span>
              <div className="h-px flex-1 bg-[#C5A059]/30" />
            </div>
            <div className="border border-[#C5A059]/40 p-5 text-left" data-testid="bank-transfer-option">
              <p className="text-xs tracking-[0.25em] uppercase text-[#9E7B3A] mb-2">Bank Transfer — ${DEPOSIT} exactly</p>
              <p className="text-xs text-[#4A4A4A]">No transaction fees. Account details are sent with your booking confirmation.</p>
            </div>
            <p className="text-[11px] text-[#4A4A4A]/70 mt-6 mb-4">Your preferred time is held for 48 hours pending deposit.</p>
            <Link to="/account" data-testid="booking-account-link"
              className="text-xs tracking-[0.25em] uppercase text-[#9E7B3A] border-b border-[#C5A059] pb-1 hover:text-[#1A1A1A] transition-colors duration-300">
              Follow this booking in Your Account →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const [selected, setSelected] = useState(null);
  const groups = [...new Set(SERVICES.map((s) => s.group))];

  return (
    <section id="services" className="px-6 py-28 md:py-40 bg-white/70 border-t border-[#C5A059]/20" data-testid="services-section">
      <Reveal className="text-center mb-20">
        <p className="text-xs tracking-[0.5em] uppercase text-[#9E7B3A] mb-6">Beauty Services</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
          The Treatment <span className="italic text-[#9E7B3A]">Menu</span>
        </h2>
        <p className="text-base font-light text-[#4A4A4A] max-w-lg mx-auto mt-6">
          In-studio treatments by appointment. A ${DEPOSIT} deposit secures every booking — pay by card (plus transaction fees) or fee-free bank transfer.
        </p>
      </Reveal>
      <div className="max-w-4xl mx-auto">
        {groups.map((g) => (
          <div key={g} className="mb-14" data-testid={`service-group-${g.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-xs tracking-[0.4em] uppercase text-[#C5A059] mb-4 px-2">{g}</p>
            <div className="divide-y divide-[#C5A059]/20 border-y border-[#C5A059]/20">
              {SERVICES.filter((s) => s.group === g).map((s, i) => (
                <Reveal key={s.n} delay={i * 0.05}>
                  <button onClick={() => setSelected(s)} data-testid={`service-${s.n}`}
                    className="group w-full text-left grid grid-cols-[auto_1fr_auto] items-baseline gap-5 md:gap-8 py-7 px-2 hover:bg-[#C5A059]/5 transition-colors duration-300">
                    <span className="font-serif text-xl font-light text-[#C5A059]">{s.n}</span>
                    <span>
                      <span className="font-serif text-2xl md:text-3xl font-light group-hover:text-[#9E7B3A] transition-colors duration-300 block mb-1.5">{s.title}</span>
                      <span className="text-sm font-light text-[#4A4A4A] leading-relaxed block">{s.text}</span>
                    </span>
                    <span className="text-right">
                      <span className="block text-xs tracking-[0.2em] uppercase text-[#4A4A4A]/70">{s.time}</span>
                      <span className="block font-serif text-lg text-[#9E7B3A] mt-1">${s.price}</span>
                      <span className="block text-[10px] tracking-[0.15em] uppercase text-[#4A4A4A]/50 mt-1">${DEPOSIT} deposit</span>
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
      <BookingModal service={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

const ContactSection = () => {
  const [profile, setProfile] = useState(getCustomerProfile());
  const [form, setForm] = useState({ service: SERVICES[0].title, message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await capi.post("/contact", form);
      if (data.success) {
        setSent(true);
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      if (err?.response?.status === 401) { clearCustomerSession(); setProfile(null); toast.error("Please log in again"); }
      else toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="px-6 py-28 md:py-36 border-t border-[#C5A059]/20" data-testid="contact-section">
      <div className="max-w-xl mx-auto">
        <Reveal className="text-center mb-12">
          <p className="text-xs tracking-[0.5em] uppercase text-[#9E7B3A] mb-6">Book a Treatment</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">
            Begin with an <span className="italic text-[#9E7B3A]">enquiry</span>
          </h2>
          <p className="text-sm font-light text-[#4A4A4A] mt-4">
            Tell us what you're dreaming of — we reply personally, right inside your account.
          </p>
        </Reveal>
        {sent ? (
          <Reveal className="flex flex-col items-center gap-4 text-center" data-testid="contact-success">
            <div className="h-px w-16 bg-[#C5A059]" />
            <p className="font-serif text-2xl font-light italic">Thank you.</p>
            <p className="text-sm text-[#4A4A4A]">Your enquiry is in your account messenger — we'll reply there shortly.</p>
            <Link to="/account" data-testid="contact-account-link"
              className="mt-2 text-xs tracking-[0.25em] uppercase text-[#9E7B3A] border-b border-[#C5A059] pb-1 hover:text-[#1A1A1A] transition-colors duration-300">
              Open Your Account →
            </Link>
          </Reveal>
        ) : !profile ? (
          <Reveal>
            <CustomerGate heading="Log in or create your account" onAuthed={setProfile} />
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <p className="text-sm text-[#4A4A4A] border-b border-[#C5A059]/30 pb-4" data-testid="contact-account-info">
                Enquiring as <span className="font-medium text-[#1A1A1A]">{profile.name}</span> · {profile.email}
              </p>
              <select value={form.service} onChange={(e) => set("service", e.target.value)} aria-label="Service" data-testid="contact-service"
                className="w-full bg-transparent border-b border-[#C5A059] pb-3 text-base text-[#1A1A1A] focus:outline-none focus:border-[#9E7B3A] transition-colors duration-300">
                {SERVICES.map((s) => <option key={s.n} value={s.title}>{s.title} — {s.price}</option>)}
                <option value="General enquiry">General enquiry</option>
              </select>
              <textarea required rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us about your skin, your lashes, your ideal time…" aria-label="Message" data-testid="contact-message"
                className="w-full bg-transparent border-b border-[#C5A059] pb-3 text-base placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#9E7B3A] transition-colors duration-300 resize-none" />
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="group w-full flex items-center justify-center gap-3 bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-4 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
                {sending ? "Sending…" : "Send Enquiry"}
                <ArrowRight className="w-4 h-4 text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const SocialLink = ({ href, label, testid, children }) => (
  <a href={href} aria-label={label} data-testid={testid}
    className="opacity-50 hover:opacity-100 hover:text-[#9E7B3A] transition-opacity duration-300">
    {children}
  </a>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.3, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="bg-[#FAFAFA] text-[#1A1A1A]" data-testid="coming-soon-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: videoY }} className="absolute inset-0 scale-110">
          <video autoPlay muted loop playsInline
            poster="https://images.unsplash.com/photo-1702476320482-0736c4b962f5?crop=entropy&cs=srgb&fm=jpg&q=85"
            className="w-full h-full object-cover" data-testid="hero-video">
            <source src="/assets/hero.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div className="absolute inset-0 bg-white/70" />

        <motion.div style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-10 flex h-full flex-col items-center justify-center px-6 pb-16">
          <motion.img
            src="/assets/logo.png" alt="Australian Luxe monogram" data-testid="brand-logo"
            className="w-24 h-24 md:w-28 md:h-28 rounded-full mb-10"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: EASE }}
          />
          <MaskedLine delay={0.3}>
            <span className="text-xs md:text-sm tracking-[0.5em] uppercase text-[#9E7B3A]">Australian Luxe</span>
          </MaskedLine>
          <h1 data-testid="now-open-header"
            className="font-serif font-light tracking-tight text-center text-5xl sm:text-6xl lg:text-8xl mt-6 mb-8 leading-[1.05]">
            <MaskedLine delay={0.5}>The Ritual</MaskedLine>
            <MaskedLine delay={0.65}><span className="italic text-[#9E7B3A]">Begins.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.1 }}
            className="text-base md:text-lg font-light text-[#4A4A4A] text-center max-w-md mb-12">
            Luxury skincare, crafted in Australia. A ritual of quiet indulgence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: EASE }}
            className="flex flex-col sm:flex-row items-center gap-4">
            <Link to="/shop" data-testid="shop-cta-button"
              className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase px-10 py-4 hover:bg-[#9E7B3A] transition-colors duration-300">
              Shop the Collection
              <ArrowRight className="w-4 h-4 text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a href="#services" data-testid="services-cta-button"
              className="inline-flex items-center gap-3 border border-[#1A1A1A]/60 text-xs tracking-[0.3em] uppercase px-10 py-4 hover:border-[#9E7B3A] hover:text-[#9E7B3A] transition-colors duration-300">
              Book a Treatment
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
          <span className="text-[10px] tracking-[0.35em] uppercase text-[#4A4A4A]/60">Scroll</span>
          <motion.div className="w-px h-10 bg-[#C5A059]"
            animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.4, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }} />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#C5A059]/30 py-5 overflow-hidden bg-white/60" data-testid="marquee-section">
        <div className="animate-marquee flex w-max items-center">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${copy}-${item}`} className="flex items-center">
                  <span className="font-serif italic font-light text-xl md:text-2xl text-[#9E7B3A] whitespace-nowrap px-8">{item}</span>
                  <span className="text-[#C5A059] text-xs">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="px-6 py-28 md:py-40" data-testid="manifesto-section">
        <Reveal className="text-center mb-20 md:mb-28">
          <p className="text-xs tracking-[0.5em] uppercase text-[#9E7B3A] mb-6">The Ritual</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
            Three quiet <span className="italic text-[#9E7B3A]">promises</span>
          </h2>
        </Reveal>
        <div className="max-w-5xl mx-auto flex flex-col gap-20 md:gap-28">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={0.1}
              className={`flex flex-col md:flex-row items-start gap-8 md:gap-16 ${i % 2 ? "md:flex-row-reverse md:text-right" : ""}`}>
              <span className="font-serif text-7xl md:text-8xl font-light text-[#C5A059]/40 leading-none">{c.n}</span>
              <div className="max-w-lg">
                <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{c.title}</h3>
                <p className="text-base font-light text-[#4A4A4A] leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FeaturedCollection />
      <ServicesSection />
      <ContactSection />

      {/* NEWSLETTER */}
      <section className="relative px-6 py-28 md:py-36 bg-white/70 border-t border-[#C5A059]/20" data-testid="newsletter-section">
        <div className="flex flex-col items-center">
          <Reveal>
            <p className="text-xs tracking-[0.5em] uppercase text-[#9E7B3A] mb-6 text-center">Join the List</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-center mb-4">
              Stay close to the <span className="italic text-[#9E7B3A]">ritual</span>
            </h2>
            <p className="text-sm font-light text-[#4A4A4A] text-center mb-12">
              New rituals, restocks and private offers — straight to your inbox.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="w-full flex justify-center">
            <NotifyForm />
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="flex flex-col items-center gap-5 py-10 border-t border-[#C5A059]/20" data-testid="site-footer">
        <div className="flex items-center gap-8">
          <SocialLink href="https://instagram.com" label="Instagram" testid="social-link-instagram"><Instagram className="w-4 h-4" /></SocialLink>
          <SocialLink href="https://facebook.com" label="Facebook" testid="social-link-facebook"><Facebook className="w-4 h-4" /></SocialLink>
          <SocialLink href="mailto:hello@australianluxe.com" label="Email" testid="social-link-email"><Mail className="w-4 h-4" /></SocialLink>
        </div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]/60">© 2026 Australian Luxe</p>
        <div className="flex items-center gap-6">
          <a href="/account" data-testid="customer-account-link"
            className="text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]/60 hover:text-[#9E7B3A] transition-colors duration-300">
            Your Account
          </a>
          <a href="/admin/login" data-testid="admin-login-link"
            className="text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]/40 hover:text-[#9E7B3A] transition-colors duration-300">
            Staff Login
          </a>
        </div>
      </footer>
    </div>
  );
}
